// Reactive lifecycle store (F-01). Wraps the engine's pure central reducer in a
// zustand store, persisted to localStorage on every change. Computed values are
// derived on read via deriveLife(), so they update reactively whenever the
// underlying data changes — never stored, never stale.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  initialLifeState,
  lifeReducer,
  deriveLife,
  type LifeState,
  type LifeAction,
  type LifeComputed,
} from '@biz-kid/engine';

type LifeStore = {
  state: LifeState;
  dispatch: (action: LifeAction) => void;
};

export const useLifeStore = create<LifeStore>()(
  persist(
    (set, get) => ({
      state: initialLifeState(),
      dispatch: (action) => set({ state: lifeReducer(get().state, action) }),
    }),
    {
      name: 'bizkid-life', // localStorage key
      version: 1,
      partialize: (s) => ({ state: s.state }), // only persist the data, not fns
      // Deep-merge so saves written before a field existed pick up its default.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as { state?: Partial<LifeState> };
        return { ...current, state: { ...current.state, ...(p.state ?? {}) } };
      },
    },
  ),
);

/** Persistent lifecycle state. */
export const useLife = (): LifeState => useLifeStore((s) => s.state);

/** Dispatch a lifecycle action through the central reducer. */
export const useLifeDispatch = (): ((action: LifeAction) => void) => useLifeStore((s) => s.dispatch);

/** Reactively-derived computed values (net worth, passive income, freedom score…). */
export const useLifeComputed = (): LifeComputed => deriveLife(useLifeStore((s) => s.state));

/** Generate a stable-enough id for new assets/liabilities/etc. (UI side). */
export function newId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${rand}`;
}
